import { Button, Card, Group, Image, Paper, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { IconCircle1, IconCircle2, IconCircle3, IconCircle4, IconDownload } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { toCanvas } from "html-to-image";
import { useRef } from "react";
import QRCode from "react-qr-code";
import { logoMaxSize } from "routes/account/route";
import { useCustomer } from "shared/graphql/cust";
import { useColorMode } from "shared/hooks/useColorMode";

const SharePanel = ({ mode }: { mode: "horizontal" | "vertical" }) => {
    const { isDark } = useColorMode();
    const theme = useMantineTheme();
    const { data: customer } = useCustomer();
    const ref = useRef<HTMLDivElement | null>(null);

    async function handleCapture() {
        if (ref.current) {
            const canvas = await toCanvas(ref.current);
            const image = canvas.toDataURL("image/png");

            const link = document.createElement("a");
            link.href = image;
            link.download = `${customer.subdomain}-alerts-${mode}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    const dimensions = mode == "horizontal" ? { width: "845px" } : { width: "375px" };

    const shareUrl = `https://${customer.subdomain}.${import.meta.env.VITE_ENVIRONMENT != "prod" ? "emns.staging" : "alerts"}.bryx.com/opt-in/register`;

    return (
        <Stack justify="center" w="100%" align="center">
            <Card p="0" shadow="md" style={{ ...dimensions }}>
                <Stack gap={0} ref={ref} bg="var(--mantine-color-body)">
                    <Group justify="center" w="100%" bg={theme.primaryColor} p="md" wrap="nowrap">
                        <Image
                            src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                            fallbackSrc={"/bryx.svg"}
                            w={logoMaxSize}
                            h={logoMaxSize}
                            fit="contain"
                            alt="Brand logo"
                        />
                        <Title>{customer.name} Alerts</Title>
                    </Group>
                    <Paper shadow="xl" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }} w="85%" mx="auto">
                        <Group p="md" wrap={mode == "vertical" ? "wrap" : "nowrap"}>
                            <Stack>
                                <Title>Sign Up In Four Easy Steps</Title>
                                <Text>
                                    Sign up to receive notifications from <strong>{customer.name}</strong>. Notifications include weather
                                    alerts, travel advisories, and other emergency notifications.
                                </Text>
                                <Stack>
                                    <Group gap="xs">
                                        <IconCircle1 style={{ color: "var(--mantine-primary-color-filled)" }} />
                                        <Title order={4}>Scan the QR Code</Title>
                                    </Group>
                                    <Group gap="xs">
                                        <IconCircle2 style={{ color: "var(--mantine-primary-color-filled)" }} />
                                        <Title order={4}>Input your phone number or email</Title>
                                    </Group>
                                    <Group gap="xs">
                                        <IconCircle3 style={{ color: "var(--mantine-primary-color-filled)" }} />
                                        <Title order={4}>Subscribe to topics</Title>
                                    </Group>
                                    <Group gap="xs">
                                        <IconCircle4 style={{ color: "var(--mantine-primary-color-filled)" }} />
                                        <Title order={4}>Start receiving notifications</Title>
                                    </Group>
                                </Stack>
                            </Stack>
                            <Stack>
                                <Paper p="sm" display="inline-flex" mx="auto" bg={isDark ? "white" : undefined}>
                                    <QRCode value={shareUrl} size={150} />
                                </Paper>
                                <Text fz="xs" ta="center" td="underline">
                                    {shareUrl}
                                </Text>
                            </Stack>
                        </Group>
                    </Paper>
                    <Group justify="center" align="center" w="100%" gap="xs" my="md">
                        <Image src="/bryx.svg" w={24} />
                        <Text fw="bold">
                            Powered by <span style={{ color: "#8f0808" }}>Bryx</span>
                        </Text>
                    </Group>
                </Stack>
            </Card>
            <Button leftSection={<IconDownload />} mx="auto" onClick={() => void handleCapture()}>
                Download
            </Button>
        </Stack>
    );
};

const Page = () => {
    return (
        <Stack m="xl">
            <SharePanel mode="horizontal" />
            <SharePanel mode="vertical" />
        </Stack>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/opt-in-share")({
    component: Page,
});
